// Night-action metadata for characters whose ability is choosing targets. Characters that only
// receive information (Empath, Chef...) or are passive (Soldier, Saint...) are left out: the
// Storyteller narrates those.
//
// firstNight / otherNight are absolute positions on the official combined night-order sheet, not
// per-script ranks, so a script mixing editions still interleaves correctly. A missing key means
// the character doesn't act that night.
//
// picks: ordered { type: "player" | "character", excludeSelf?, optional?, label? }. Players are
// picked from the seats, characters from the script. The app only records picks; the Storyteller
// confirms and applies any effect.
//
// oncePerGame is a display hint only and isn't enforced.
export const NIGHT_ACTIONS = {
    // --- Travelers ---
    bureaucrat: {
        firstNight: 3.6, otherNight: 1.3,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a player (not yourself) whose vote counts as 3 votes tomorrow."
    },
    thief: {
        firstNight: 3.8, otherNight: 1.4,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a player (not yourself) whose vote counts negatively tomorrow."
    },
    harlot: {
        otherNight: 1.5,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a living player to ask for their character.",
        note: "If they agree, you learn it, and you might both die."
    },
    bonecollector: {
        otherNight: 1.6,
        picks: [{ type: "player", optional: true, label: "Dead player (optional)" }],
        prompt: "You may choose a dead player who regains their ability until dusk.",
        oncePerGame: true
    },
    // --- Trouble Brewing ---
    poisoner: {
        firstNight: 23, otherNight: 8,
        picks: [{ type: "player" }],
        prompt: "Choose a player to poison.",
        appliesStatus: "poisoned",
        statusLasts: "dusk"
    },
    fortuneteller: {
        firstNight: 47, otherNight: 67,
        picks: [{ type: "player" }, { type: "player" }],
        prompt: "Choose two players to check for the Demon."
    },
    butler: {
        firstNight: 48, otherNight: 81,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a player (not yourself) -- you can only vote tomorrow if they do too."
    },
    monk: {
        otherNight: 15,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a player (not yourself) to protect from the Demon tonight.",
        appliesStatus: "protected"
    },
    imp: {
        otherNight: 31,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        note: "You may choose yourself -- a Minion becomes the new Imp."
    },
    ravenkeeper: {
        otherNight: 65,
        picks: [{ type: "player" }],
        prompt: "Choose a player to learn the true character of.",
        note: "Only relevant if you died at night."
    },
    // --- Bad Moon Rising ---
    lunatic: {
        firstNight: 12, otherNight: 26,
        picks: [{ type: "player" }],
        prompt: "Secretly act like the Demon: choose a player.",
        note: "No real effect. Mainly relevant with 7+ players."
    },
    sailor: {
        firstNight: 16, otherNight: 4,
        picks: [{ type: "player" }],
        prompt: "Choose yourself or another player to be unable to use their ability until dusk.",
        appliesStatus: "drunk",
        statusLasts: "dusk"
    },
    courtier: {
        firstNight: 25, otherNight: 9,
        picks: [{ type: "character" }],
        prompt: "Choose a character to disable for 3 days and nights.",
        oncePerGame: true
    },
    devilsadvocate: {
        firstNight: 30, otherNight: 17,
        picks: [{ type: "player" }],
        prompt: "Choose a living player who will survive tomorrow's execution.",
        note: "Must differ from last night's choice."
    },
    pukka: {
        firstNight: 37, otherNight: 33,
        picks: [{ type: "player" }],
        prompt: "Choose a player to poison.",
        note: "Whoever you poisoned last night dies tonight.",
        // Only the poison half is unconditional; the kill still needs the Storyteller's judgment.
        appliesStatus: "poisoned",
        statusLasts: "replaced"
    },
    chambermaid: {
        firstNight: 66, otherNight: 85,
        picks: [{ type: "player" }, { type: "player" }],
        prompt: "Choose two players to learn how many used an ability tonight."
    },
    innkeeper: {
        otherNight: 10,
        picks: [{ type: "player" }, { type: "player" }],
        prompt: "Choose two players to protect.",
        note: "One of them can't use their ability tomorrow.",
        // Both picks are unconditionally safe from dying tonight -- it's only *which one* goes drunk
        // that's the Storyteller's own secret choice, so that half stays manual.
        appliesStatus: "protected"
    },
    gambler: {
        otherNight: 12,
        picks: [{ type: "player" }, { type: "character", label: "Guess their character" }],
        prompt: "Choose a player and guess their character.",
        note: "You die if you guess wrong."
    },
    exorcist: {
        otherNight: 27,
        picks: [{ type: "player" }],
        prompt: "Choose a player you believe is the Demon.",
        note: "Must differ from last night's choice; no effect on night 1."
    },
    zombuul: {
        otherNight: 32,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        note: "Only if no one died today."
    },
    shabaloth: {
        otherNight: 34,
        picks: [
            { type: "player" }, { type: "player" },
            { type: "player", optional: true, label: "Player to bring back (optional)" }
        ],
        prompt: "Choose two players to kill, and optionally revive one player you killed last night."
    },
    po: {
        otherNight: 35,
        picks: [{ type: "player" }, { type: "player", optional: true }, { type: "player", optional: true }],
        prompt: "Choose a player to kill -- or up to three if you killed no one last night."
    },
    assassin: {
        otherNight: 47,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        oncePerGame: true
    },
    godfather: {
        otherNight: 48,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        note: "Only if an Outsider died today."
    },
    professor: {
        otherNight: 56,
        picks: [{ type: "player" }],
        prompt: "Choose a dead Townsfolk to bring back to life.",
        oncePerGame: true
    },
    // --- Sects & Violets ---
    philosopher: {
        firstNight: 5, otherNight: 2,
        picks: [{ type: "character" }],
        prompt: "Choose a good character whose ability you permanently gain.",
        oncePerGame: true
    },
    snakecharmer: {
        firstNight: 27, otherNight: 14,
        picks: [{ type: "player" }],
        prompt: "Choose a player; if it's the Demon, you swap characters and alignments."
    },
    witch: {
        firstNight: 32, otherNight: 18,
        picks: [{ type: "player" }],
        prompt: "Choose a player to curse.",
        note: "Only while 4 or more players are alive."
    },
    cerenovus: {
        firstNight: 33, otherNight: 19,
        picks: [{ type: "player" }, { type: "character", label: "Character they must act like" }],
        prompt: "Choose a player and a character for them to act like tomorrow."
    },
    dreamer: {
        firstNight: 51, otherNight: 69,
        picks: [{ type: "player", excludeSelf: true }],
        prompt: "Choose a player (not yourself) to learn one good and one evil character they might be."
    },
    seamstress: {
        firstNight: 52, otherNight: 73,
        picks: [{ type: "player" }, { type: "player" }],
        prompt: "Choose two players to learn if they share an alignment.",
        oncePerGame: true
    },
    pithag: {
        otherNight: 20,
        picks: [{ type: "player" }, { type: "character", label: "New character to turn them into" }],
        prompt: "Choose a player and a new character to turn them into.",
        note: "Not on night 1."
    },
    fanggu: {
        otherNight: 36,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        note: "The first time this kills an Outsider, they become the new Fang Gu instead of dying."
    },
    nodashii: {
        otherNight: 37,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill."
    },
    vortox: {
        otherNight: 38,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill."
    },
    vigormortis: {
        otherNight: 40,
        picks: [{ type: "player" }],
        prompt: "Choose a player to kill.",
        note: "Also disables a neighboring Townsfolk's ability."
    }
};
//# sourceMappingURL=nightActions.js.map